/**
 * Sculk shrieker (vanilla): listens for player-made vibrations within 8 blocks. On a vibration it shrieks (90 tick
 * cooldown), darkens every survival player within 40 blocks, and - only if it is a naturally generated one
 * (`can_summon`, the SCULK_SHRIEKER_SUMMON_ID block; a player-placed one never summons) - warns the player.
 *
 * Warning level 0..4 per player, +1 per shriek at most every 200 ticks, -1 per 10 minutes. Reaching 4 with no
 * warden within 24 blocks raises one within ~5 blocks of the shrieker (and clears the player's warnings).
 */

import { collisionOf, SCULK_SHRIEKER_ID, SCULK_SHRIEKER_SUMMON_ID } from '../../blocks';
import type { World } from '../../world/chunk';
import type { PlayerTarget } from '../host';
import { wardenHooks, type VibrationKind } from './wardenHooks';

export const SHRIEK_RANGE = 8;
export const SHRIEK_COOLDOWN = 90;
export const WARN_COOLDOWN = 200;
export const WARN_DECAY = 12000;
export const WARN_MAX = 4;
export const WARDEN_NEAR = 24;
export const SHRIEK_DARKNESS_RANGE = 40;
export const SHRIEK_DARKNESS_TICKS = 260;

/** One player's warning level (vanilla WardenSpawnTracker). Decay is lazy, from the tick counter. */
export class WarningTracker {
  private level = 0;
  private lastWarn = -1e9;
  private lastDecay = 0;

  levelAt(tick: number): number {
    if (this.level > 0) {
      const n = Math.floor((tick - this.lastDecay) / WARN_DECAY);
      if (n > 0) { this.level = Math.max(0, this.level - n); this.lastDecay += n * WARN_DECAY; }
    } else this.lastDecay = tick;
    return this.level;
  }

  /** Raise by one unless warned less than 200 ticks ago or already at 4. Returns whether the level went up. */
  tryWarn(tick: number): boolean {
    const l = this.levelAt(tick);
    if (tick - this.lastWarn < WARN_COOLDOWN || l >= WARN_MAX) return false;
    if (l === 0) this.lastDecay = tick;
    this.level = l + 1;
    this.lastWarn = tick;
    return true;
  }

  clear(): void { this.level = 0; }
}

const trackers = new Map<string, WarningTracker>();
const readyAt = new Map<string, number>();

const trackerOf = (id: string): WarningTracker => {
  let t = trackers.get(id);
  if (!t) { t = new WarningTracker(); trackers.set(id, t); }
  return t;
};

export const warningLevel = (id: string, tick = wardenHooks.tick()): number => trackers.get(id)?.levelAt(tick) ?? 0;
/** Forget all warnings and shrieker cooldowns (new world, tests). */
export function resetShriekers(): void { trackers.clear(); readyAt.clear(); }

const valid = (p: PlayerTarget): boolean => p.alive && !p.creative && !p.spectator;

function wardenNear(x: number, y: number, z: number): boolean {
  for (const m of wardenHooks.mobs()) {
    if (m.type !== 'warden' || m.removed || m.dying) continue;
    if (Math.hypot(m.x - x, m.z - z) <= WARDEN_NEAR && Math.abs(m.y - y) <= WARDEN_NEAR) return true;
  }
  return false;
}

/** A spot within ~5 blocks of the shrieker with room for a 0.9 x 2.9 body on solid ground. */
export function wardenSpot(w: World, x: number, y: number, z: number, rnd: () => number): [number, number, number] | null {
  for (let t = 0; t < 40; t++) {
    const cx = x + Math.floor(rnd() * 11) - 5, cz = z + Math.floor(rnd() * 11) - 5;
    for (let dy = 4; dy >= -4; dy--) {
      const fy = y + dy;
      if (collisionOf(w.getBlock(cx, fy - 1, cz)).length === 0) continue;
      if (collisionOf(w.getBlock(cx, fy, cz)).length || collisionOf(w.getBlock(cx, fy + 1, cz)).length || collisionOf(w.getBlock(cx, fy + 2, cz)).length) continue;
      return [cx + 0.5, fy, cz + 0.5];
    }
  }
  return null;
}

function shriek(w: World, bx: number, by: number, bz: number, canSummon: boolean, source: string): void {
  const key = `${bx},${by},${bz}`;
  const now = wardenHooks.tick();
  if ((readyAt.get(key) ?? -1) > now) return;
  readyAt.set(key, now + SHRIEK_COOLDOWN);
  const cx = bx + 0.5, cy = by + 0.5, cz = bz + 0.5;
  wardenHooks.sound('block.sculk_shrieker.shriek', cx, cy, cz);
  const players = wardenHooks.players();
  for (const p of players) {
    if (!valid(p)) continue;
    if (Math.hypot(p.x - cx, p.z - cz) <= SHRIEK_DARKNESS_RANGE && Math.abs(p.y - cy) <= SHRIEK_DARKNESS_RANGE) wardenHooks.darkness(p, SHRIEK_DARKNESS_TICKS);
  }
  if (!canSummon || wardenHooks.difficulty() === 'peaceful') return;
  const who = players.find((p) => p.id === source);
  if (!who || !valid(who) || wardenNear(cx, cy, cz)) return;
  const tr = trackerOf(source);
  if (!tr.tryWarn(now)) return;
  if (tr.levelAt(now) < WARN_MAX) return;
  const spot = wardenSpot(w, bx, by, bz, wardenHooks.rnd);
  if (!spot) return;
  if (wardenHooks.spawn('warden' as never, spot[0], spot[1], spot[2])) tr.clear();
}

const sink = (x: number, y: number, z: number, _kind: VibrationKind, source: string | null): void => {
  if (source === null) return; // only a player's doing wakes a shrieker
  const w = wardenHooks.world();
  if (!w) return;
  const bx = Math.floor(x), by = Math.floor(y), bz = Math.floor(z);
  const r = SHRIEK_RANGE;
  for (let dy = -r; dy <= r; dy++) {
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx * dx + dy * dy + dz * dz > r * r) continue;
        const id = w.getBlock(bx + dx, by + dy, bz + dz);
        if (id === SCULK_SHRIEKER_ID || id === SCULK_SHRIEKER_SUMMON_ID) shriek(w, bx + dx, by + dy, bz + dz, id === SCULK_SHRIEKER_SUMMON_ID, source);
      }
    }
  }
};

/** Start listening for vibrations (idempotent; EntityManager calls it). */
export function installShriekers(): void { wardenHooks.sinks.add(sink); }
